import type {
    ComponentExportStructure,
    CoreExportFormat,
    Dependencies,
    Namespace,
    ReferencedItem
} from '@wix/document-services-types'
import {VARIANTS_DATA_TYPES} from '@wix/format-converter-schemas'
import _ from 'lodash'
import type {DecoratedExportFormat, DecorationPluginFunction, VariantsMap} from '../..'
import {forEachComponentDFSWithVariantsContext, getDefaultBreakpoint} from './utils'
import {
    BREAKPOINT_RANGE_TYPE,
    NAMESPACE_VARIANT_SOURCE,
    PRESET_TYPE,
    TYPE_TO_VARIANT_MAP_FEATURE,
    VARIANTS_MAP_PROPS_BY_TYPE
} from './constants'

const collectVariantsToMapFromDependencies = (variantsMap: VariantsMap, dependencies: Dependencies): void => {
    NAMESPACE_VARIANT_SOURCE.forEach((namespace: Namespace) => {
        if (dependencies?.references?.[namespace]) {
            _.forOwn(dependencies.references[namespace], (variant: ReferencedItem) => {
                if (variant.type === BREAKPOINT_RANGE_TYPE || variant.type === PRESET_TYPE) {
                    const feature = TYPE_TO_VARIANT_MAP_FEATURE[variant.type]
                    variantsMap[feature] ??= {}
                    variantsMap[feature]![variant.target] = {
                        ...(variant.info ?? {}),
                        id: variant.target
                    }
                }
            })
        }
    })
}

const collectVariantsToMapFromComponent = (component: ComponentExportStructure, variantsMap: VariantsMap) => {
    NAMESPACE_VARIANT_SOURCE.forEach(namespace => {
        if (!component[namespace]) {
            return
        }
        const {values} = component[namespace]

        _.merge(
            variantsMap,
            _(values)
                .filter(variant => _.isString(variant.type) && !!TYPE_TO_VARIANT_MAP_FEATURE[variant.type])
                .groupBy(variant => TYPE_TO_VARIANT_MAP_FEATURE[variant.type])
                .mapValues(variantGroup =>
                    _(variantGroup)
                        .keyBy(variant => variant.id)
                        .mapValues(variant => _.pick(variant, VARIANTS_MAP_PROPS_BY_TYPE[variant.type]))
                        .value()
                )
                .value()
        )

        const collectedIds = Object.values(variantsMap).flatMap(variant =>
            Object.values(variant).map(value => value.id)
        )

        component[namespace] = _.map(values, value => {
            // no hash, since this is not really a reference
            return collectedIds.includes(value.id) ? {id: value.id} : value
        })
    })
}

export const pack: DecorationPluginFunction = (componentExport: CoreExportFormat): void => {
    const {dependencies} = componentExport

    const variantsMap: VariantsMap = {
        breakpoints: {},
        presets: {}
    }

    if (dependencies?.references) {
        collectVariantsToMapFromDependencies(variantsMap, dependencies)
    }

    forEachComponentDFSWithVariantsContext(
        componentExport,
        component => component?.breakpointVariants?.values ?? component?.variants,
        (component, breakpointsInContext) => {
            collectVariantsToMapFromComponent(component, variantsMap)

            const defaultBreakpoint = getDefaultBreakpoint(breakpointsInContext ?? [])

            _.forOwn(VARIANTS_DATA_TYPES, (namespace: string) => {
                const item = component[namespace]

                if (!item) {
                    return
                }

                const {default: defaultValue} = item

                if (defaultValue && defaultBreakpoint) {
                    const defaultWithBreakpoint = {
                        value: defaultValue,
                        conditions: [`#${defaultBreakpoint.id}`]
                    }
                    delete item.default
                    item.variants = item.variants ?? []
                    item.variants.push(defaultWithBreakpoint)
                }
            })
        }
    )
    ;(componentExport as DecoratedExportFormat).variantsMap = variantsMap
}
